import React, {
  useState,
  InputHTMLAttributes,
  AriaAttributes,
  useCallback,
} from 'react';
import { cn } from '@express-labs/raven-tools';
import { WrappedFieldMetaProps, WrappedFieldInputProps } from 'redux-form';
import s from './DeprecatedCheckbox.scss';
import Loader from '../Loader/Loader';

let instance = 0;
export type DeprecatedRawCheckboxProps = {
  'aria-label'?: string;
  checkedRectClassName?: string;
  id?: string;
  input?: InputHTMLAttributes<HTMLInputElement & WrappedFieldInputProps>;
  inputClassName?: string;
  isChecked?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: React.ReactNode;
  labelClassName?: string;
  loaderClassName?: string;
  meta?: MetaType | WrappedFieldMetaProps;
  name?: string;
  onClick?: (
    event:
      | React.MouseEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLInputElement>
  ) => Promise<void> | void;
  showDeprecatedCheckbox?: boolean;
  uncheckedRectClassName?: string;
  waitForOnClick?: boolean;
};
const RawDeprecatedCheckbox: React.FC<DeprecatedRawCheckboxProps> = ({
  'aria-label': ariaLabel,
  checkedRectClassName,
  id: inputId,
  input,
  inputClassName,
  isDisabled,
  label,
  labelClassName,
  loaderClassName,
  meta,
  name: inputName,
  uncheckedRectClassName,
  isRequired = false,
  showDeprecatedCheckbox = true,
  onClick,
  waitForOnClick = false,
  isChecked = false,
}) => {
  instance += 1;
  const [waitingForOnClick, setWaitingForOnClick] = useState<boolean>(false);
  const [checked, setIsChecked] = useState(isChecked);

  const handleClick = useCallback(
    (
      ev:
        | React.MouseEvent<HTMLInputElement>
        | React.ChangeEvent<HTMLInputElement>
    ) => {
      ev.persist();
      if (waitForOnClick && onClick) {
        if (!waitingForOnClick) {
          setWaitingForOnClick(true);
          onClick(ev)
            ?.then(() => {
              setWaitingForOnClick(false);
              setIsChecked(!checked);
            })
            .catch(() => {
              setWaitingForOnClick(false);
              setIsChecked(checked);
            });
        }
      } else {
        setIsChecked(!checked);
        onClick?.(ev);
      }
    },
    [waitForOnClick, onClick]
  );

  const getRectClassName = (checkedCn: string, uncheckedCn: string) => {
    if (isDisabled) {
      return cn(s.uncheckedRect, s.disabled);
    }
    return checked ? checkedCn : uncheckedCn;
  };
  const labelClassNames = ['labelActive'];
  const inputClassNames = [s.input];
  const uncheckedRectClassNames = [s.uncheckedRect];
  const checkedRectClassNames = [s.checkedRect];
  const loaderClassNames = [s.loader];
  if (meta?.error) {
    inputClassNames.push('has-error');
  }
  if (waitingForOnClick) {
    labelClassNames.push(s.waitingLabel);
    checkedRectClassNames.push(s.hidden);
    uncheckedRectClassNames.push(s.hidden);
  } else if (isDisabled) {
    labelClassNames.push(s.waitingLabel);
    loaderClassNames.push(s.hidden);
    inputClassNames.push(s.disabled);
    checkedRectClassNames.push(s.disabled);
    uncheckedRectClassNames.push(s.disabled);
  } else {
    labelClassNames.push(s.label);
    loaderClassNames.push(s.hidden);
  }
  if (labelClassName) {
    labelClassNames.push(labelClassName);
  }
  if (inputClassName) {
    inputClassNames.push(inputClassName);
  }
  if (checkedRectClassName) {
    checkedRectClassNames.push(checkedRectClassName);
  }
  if (uncheckedRectClassName) {
    uncheckedRectClassNames.push(uncheckedRectClassName);
  }
  if (loaderClassName) {
    loaderClassNames.push(loaderClassName);
  }
  const fullLabelClassName = labelClassNames.join(' ');
  const fullInputClassName = inputClassNames.join(' ');
  const fullCheckedRectClassName = checkedRectClassNames.join(' ');
  const fullUncheckedRectClassName = uncheckedRectClassNames.join(' ');
  const fullLoaderClassName = loaderClassNames.join(' ');

  const aria: AriaAttributes = {};
  if (waitingForOnClick) {
    aria['aria-busy'] = true;
    aria['aria-disabled'] = true;
  }
  if (waitForOnClick) {
    aria['aria-live'] = 'assertive';
  }
  const name = inputName || `rvn-chkbx-${instance}`;
  const id = inputId || inputName || input?.name || `rvn-chkbx-${instance}`;

  const newAriaLabel = meta?.error
    ? `${ariaLabel || label}: ${meta.error}`
    : ariaLabel;

  return (
    <div>
      {meta?.touched && meta.error && (
        <strong className="labelError">{meta.error} </strong>
      )}
      <div>
        <input
          {...input}
          {...aria}
          aria-label={newAriaLabel}
          checked={checked}
          className={fullInputClassName}
          disabled={isDisabled}
          id={id}
          name={name}
          required={isRequired}
          type="checkbox"
          onChange={handleClick}
        />
        {showDeprecatedCheckbox && (
          <svg
            className={cn(s.checkboxSmall, checked ? s.checked : s.unchecked)}
            viewBox="0 0 16 16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M.5.5h15v15H.5z"
              className={getRectClassName(
                fullCheckedRectClassName,
                fullUncheckedRectClassName
              )}
            />
            {checked && (
              <path
                fill="#fff"
                d="M4.565 6.752 3 8.367 7.167 12 14 4.426 12.243 3 6.968 8.847z"
              />
            )}
          </svg>
        )}
        {waitingForOnClick && (
          <Loader
            className={cn(fullLoaderClassName)}
            display="inline"
            size="small"
          />
        )}
        <label className={cn(fullLabelClassName)} htmlFor={id}>
          {isRequired && <span className="labelError">&nbsp;*&nbsp;</span>}
          {label}
        </label>
      </div>
    </div>
  );
};

export default RawDeprecatedCheckbox;
